import React, { useState, useEffect } from 'react';
import { Trophy, Award, Star, Lock, TrendingUp } from 'lucide-react';
import { studentAPI } from '../../services/api';

const Achievements = () => {
    const [achievements, setAchievements] = useState([]);
    const [stats, setStats] = useState({ total: 0, earned: 0, percentage: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'earned', 'locked'

    useEffect(() => {
        fetchAchievements();
    }, []);

    const fetchAchievements = async () => {
        try {
            setLoading(true);
            const response = await studentAPI.getAchievements();
            const data = response.data.achievements || [];
            setAchievements(data);
            
            const earned = data.filter(a => a.earned).length;
            setStats({
                total: data.length,
                earned: earned,
                percentage: Math.round((earned / data.length) * 100)
            });
        } catch (err) {
            console.error('Failed to fetch achievements:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredAchievements = achievements.filter(a => {
        if (filter === 'earned') return a.earned;
        if (filter === 'locked') return !a.earned;
        return true;
    });

    const getCategoryColor = (category) => {
        const colors = {
            streak: '#FF6B35',
            learning: '#27B05B',
            social: '#6C5CE7',
            mastery: '#FFD700',
            milestone: '#4ECDC4'
        };
        return colors[category] || '#666';
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Loading achievements...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Trophy size={40} color="#FFD700" />
                <h1 style={styles.title}>Achievements & Badges</h1>
            </div>

            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <Award size={32} color="#27B05B" />
                    <div style={styles.statValue}>{stats.earned}</div>
                    <div style={styles.statLabel}>Earned</div>
                </div>
                <div style={styles.statCard}>
                    <Lock size={32} color="#999" />
                    <div style={styles.statValue}>{stats.total - stats.earned}</div>
                    <div style={styles.statLabel}>Locked</div>
                </div>
                <div style={styles.statCard}>
                    <TrendingUp size={32} color="#6C5CE7" />
                    <div style={styles.statValue}>{stats.percentage}%</div>
                    <div style={styles.statLabel}>Completion</div>
                </div>
            </div>

            <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: `${stats.percentage}%`}}></div>
            </div>

            <div style={styles.filters}>
                <button
                    onClick={() => setFilter('all')}
                    style={{...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {})}}
                >
                    All ({achievements.length})
                </button>
                <button
                    onClick={() => setFilter('earned')}
                    style={{...styles.filterBtn, ...(filter === 'earned' ? styles.filterBtnActive : {})}}
                >
                    Earned ({stats.earned})
                </button>
                <button
                    onClick={() => setFilter('locked')}
                    style={{...styles.filterBtn, ...(filter === 'locked' ? styles.filterBtnActive : {})}}
                >
                    Locked ({stats.total - stats.earned})
                </button>
            </div>

            <div style={styles.achievementsGrid}>
                {filteredAchievements.map(achievement => (
                    <div
                        key={achievement.id}
                        style={{
                            ...styles.achievementCard,
                            ...(achievement.earned ? styles.achievementEarned : styles.achievementLocked)
                        }}
                    >
                        <div
                            style={{
                                ...styles.badgeIcon,
                                backgroundColor: achievement.earned 
                                    ? getCategoryColor(achievement.category) 
                                    : '#E0E0E0'
                            }}
                        >
                            {achievement.earned ? (
                                <span style={styles.icon}>{achievement.icon || '🏆'}</span>
                            ) : (
                                <Lock size={32} color="#999" />
                            )}
                        </div>

                        <h3 style={styles.achievementTitle}>{achievement.title}</h3>
                        <p style={styles.achievementDescription}>{achievement.description}</p>

                        {achievement.earned ? (
                            <div style={styles.earnedBadge}>
                                <Award size={16} color="#27B05B" />
                                <span>Earned on {new Date(achievement.earned_at).toLocaleDateString()}</span>
                            </div>
                        ) : (
                            <div style={styles.progressSection}>
                                <div style={styles.progressText}>
                                    {achievement.current || 0} / {achievement.target || 0}
                                </div>
                                <div style={styles.miniProgressBar}>
                                    <div
                                        style={{
                                            ...styles.miniProgressFill,
                                            width: `${Math.min(((achievement.current || 0) / (achievement.target || 1)) * 100, 100)}%`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        <div style={styles.categoryBadge}>
                            {achievement.category}
                        </div>
                    </div>
                ))}
            </div>

            {filteredAchievements.length === 0 && (
                <div style={styles.empty}>
                    <Trophy size={64} color="#ccc" />
                    <h3>No achievements found</h3>
                    <p>Keep learning to unlock new badges!</p>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '30px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50',
        margin: 0
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
    },
    statCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '25px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50',
        margin: '10px 0 5px'
    },
    statLabel: {
        fontSize: '14px',
        color: '#666',
        fontWeight: '500'
    },
    progressBar: {
        width: '100%',
        height: '15px',
        backgroundColor: '#E0E0E0',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '30px'
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#27B05B',
        transition: 'width 0.5s ease',
        borderRadius: '10px'
    },
    filters: {
        display: 'flex',
        gap: '10px',
        marginBottom: '30px',
        flexWrap: 'wrap'
    },
    filterBtn: {
        padding: '10px 20px',
        backgroundColor: 'white',
        color: '#666',
        border: '2px solid #E0E0E0',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    filterBtnActive: {
        backgroundColor: '#27B05B',
        color: 'white',
        borderColor: '#27B05B'
    },
    achievementsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px'
    },
    achievementCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '25px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        position: 'relative'
    },
    achievementEarned: {
        borderTop: '4px solid #27B05B'
    },
    achievementLocked: {
        opacity: 0.7,
        filter: 'grayscale(50%)'
    },
    badgeIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    icon: {
        fontSize: '40px'
    },
    achievementTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#2C3E50',
        margin: '0 0 10px 0'
    },
    achievementDescription: {
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.5',
        margin: '0 0 15px 0'
    },
    earnedBadge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '8px',
        backgroundColor: '#F0F9F4',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#27B05B',
        fontWeight: '600'
    },
    progressSection: {
        marginTop: '15px'
    },
    progressText: {
        fontSize: '13px',
        color: '#666',
        marginBottom: '8px',
        fontWeight: '600'
    },
    miniProgressBar: {
        width: '100%',
        height: '8px',
        backgroundColor: '#E0E0E0',
        borderRadius: '4px',
        overflow: 'hidden'
    },
    miniProgressFill: {
        height: '100%',
        backgroundColor: '#27B05B',
        transition: 'width 0.5s ease'
    },
    categoryBadge: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        padding: '4px 12px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#666',
        textTransform: 'uppercase'
    },
    empty: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#999'
    },
    loading: {
        textAlign: 'center',
        padding: '60px 20px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
    }
};

export default Achievements;
